import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/base/SEO';
import { aboutData, aboutTimeline } from '@/mocks/about';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ky体育',
    url: 'https://www.ky-sports.com',
    description: 'ky体育是专业的在线体育平台，提供丰富的体育赛事、实时数据、竞技资讯与互动体验。',
    email: 'contact@ky-sports.com',
    telephone: '400-888-6666',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座2001室',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    foundingDate: '2018',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutData.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于ky体育 - 官方体育竞技平台"
        description="了解ky体育的品牌故事、发展历程、企业荣誉与联系方式。ky体育成立于2018年，是专业的在线体育竞技平台，月活用户超过500万。"
        keywords="ky体育,ky体育官网,关于ky体育,ky体育平台,ky体育联系方式"
        canonicalUrl="https://www.ky-sports.com/about"
        schemaData={[orgSchema, faqSchema]}
      />
      <Navbar />
      <main className="bg-background-50">
        <div className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Intro */}
          <section className="w-full px-4 md:px-6 py-12">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 text-center">
                {aboutData.brandIntro.title}
              </h1>
              <div className="mt-10 space-y-5 text-sm md:text-base text-foreground-700 leading-loose">
                {aboutData.brandIntro.paragraphs.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="w-full px-4 md:px-6 py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {aboutTimeline.map((item, idx) => (
                    <div
                      key={idx}
                      className={`relative flex items-start gap-6 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="hidden md:flex md:w-1/2"></div>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-1.5 mt-1.5 z-10 ring-4 ring-background-100"></div>
                      <div className="ml-10 md:ml-0 md:w-1/2 bg-background-50 border border-background-200 rounded-xl p-5">
                        <span className="inline-block bg-primary-500 text-white text-xs px-3 py-1 rounded-full mb-2">
                          {item.year}
                        </span>
                        <p className="text-sm text-foreground-700">{item.event}</p>
                      </div>
                      {idx % 2 !== 0 && <div className="hidden md:flex md:w-1/2"></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Honors */}
          <section className="w-full px-4 md:px-6 py-14 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
                荣誉资质
              </h2>
              <p className="text-center text-sm text-foreground-500 mb-12">
                多年来ky体育凭借卓越的产品与服务，赢得了行业与用户的广泛认可
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutData.honors.map((honor, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 border border-background-200 rounded-xl p-6 text-center hover:border-accent-300 transition-all duration-300"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-50 flex items-center justify-center">
                      <i className={`${honor.icon} text-xl text-accent-500`}></i>
                    </div>
                    <span className="text-xs text-accent-600 font-medium bg-accent-50 px-2.5 py-0.5 rounded-full">
                      {honor.year}
                    </span>
                    <h3 className="mt-3 font-semibold text-foreground-950 text-sm">{honor.title}</h3>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{honor.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full px-4 md:px-6 py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
                {aboutData.contact.title}
              </h2>
              <p className="text-center text-sm text-foreground-500 mb-12">
                如果您有任何问题或合作意向，欢迎通过以下方式与我们取得联系
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-background-50 border border-background-200 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-50 flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-primary-500"></i>
                  </div>
                  <h4 className="font-semibold text-foreground-950 text-sm">电子邮箱</h4>
                  <p className="mt-2 text-sm text-foreground-500">{aboutData.contact.email}</p>
                </div>

                <div className="bg-background-50 border border-background-200 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-50 flex items-center justify-center">
                    <i className="ri-phone-line text-xl text-primary-500"></i>
                  </div>
                  <h4 className="font-semibold text-foreground-950 text-sm">客服电话</h4>
                  <p className="mt-2 text-sm text-foreground-500">{aboutData.contact.phone}</p>
                </div>

                <div className="bg-background-50 border border-background-200 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-50 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl text-primary-500"></i>
                  </div>
                  <h4 className="font-semibold text-foreground-950 text-sm">公司地址</h4>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                    {aboutData.contact.address}
                  </p>
                </div>
              </div>

              <div className="mt-8 bg-background-50 border border-background-200 rounded-xl p-6 text-center">
                <h4 className="font-semibold text-foreground-950 text-sm">工作时间</h4>
                <p className="mt-2 text-sm text-foreground-500">{aboutData.contact.workHours}</p>
                <div className="mt-4 flex items-center justify-center gap-4">
                  {aboutData.contact.socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer text-foreground-500"
                      rel="nofollow"
                    >
                      <i className={`${social.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-14 bg-background-50">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
                常见问题
              </h2>
              <p className="text-center text-sm text-foreground-500 mb-10">
                以下是一些常见问题的解答，帮助您更好地了解ky体育
              </p>
              <div className="space-y-3">
                {aboutData.faq.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 border border-background-200 rounded-xl overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    >
                      <span className="text-sm font-medium text-foreground-900 pr-4">{item.question}</span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 text-lg shrink-0 ${
                          openFaq === idx ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {openFaq === idx && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}